import clsx from "clsx";
import { format } from "date-fns";
import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { z } from "zod";

import { usePost } from "../../../api-client/usePost";
import { DealerOrderPreview } from "../../../queries/usePreviewDealerOrderFromCart";
import { useDealerAddress } from "../../../stores/hooks/useDealerAddress";
import { useNullableDealerAccountInfo } from "../../../stores/hooks/useNullableDealerAccountInfo";
import { useSetErrorToast } from "../../../stores/hooks/useSetErrorToast";
import { useSetInfoToast } from "../../../stores/hooks/useSetInfoToast";
import { PrimaryButton } from "../../../ui/Button/Button";
import { Input } from "../../../ui/Input/Input";
import { KeyValue } from "../../../ui/KeyValue/KeyValue";
import { PriceDisplay } from "../../../ui/PriceDisplay/PriceDisplay";
import { useForm } from "../../../util/useForm";
import { myOrdersHash } from "../../MyQuotes/QuotesDialog";
import { NormalizedDealerOrder } from "./useNormalizedDealerOrder";
import { useTotals } from "./useTotals";

const schema = z.object({
  customerEmail: z.union([z.string().email(), z.string().optional()]),
  customerPhone: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  poNumber: z.string().nonempty({ message: "dealerOrderModal.error.poNumber" }),
});

type DealerOrderFormProps = {
  data: DealerOrderPreview;
  hide: VoidFunction;
  normalizedData: NormalizedDealerOrder[];
};

export const DealerOrderForm: FunctionComponent<DealerOrderFormProps> = ({
  data,
  hide,
  normalizedData,
}) => {
  const [, setLocation] = useLocation();
  const dealerAddress = useDealerAddress();
  const dealer = useNullableDealerAccountInfo();
  const { t } = useTranslation();
  const { loading: cartIsSaving, send: saveCart } = usePost<DealerOrderPreview>(
    "/retailSiteWebService/shopping/cart/saveCart.php"
  );
  const { loading: dealerOrderIsLoading, send: generateDealerOrder } =
    usePost<DealerOrderPreview>(
      "/retailSiteWebService/shopping/dealerOrder/generateDealerOrderFromCart.php"
    );
  const setInfoToast = useSetInfoToast();
  const setErrorToast = useSetErrorToast();
  const [handleForm] = useForm({
    noReset: true,
    onSubmit({
      customerEmail = "",
      customerPhone = "",
      firstName,
      lastName,
      poNumber = "",
    }) {
      const promises: Promise<unknown>[] = [];
      if ([firstName, lastName, customerPhone, customerEmail].some(Boolean)) {
        promises.push(
          saveCart({
            appointmentRequestTime: format(new Date(), "yyyy-MM-dd"),
            cartId: data.orderNumber,
            customerContactMethodPreference:
              data.customerContactMethodPreference,
            customerEmail,
            customerName: [firstName, lastName].filter(Boolean).join(" "),
            customerPhone,
            dealerId: dealer?.dealerId ?? "",
            notes: "",
            orderNumber: data.orderNumber,
          })
        );
      }

      promises.push(
        generateDealerOrder({
          appointmentRequestTime: "",
          cartId: data.orderNumber,
          customerContactMethodPreference: data.customerContactMethodPreference,
          customerEmail,
          customerName: [firstName, lastName].filter(Boolean).join(" "),
          customerPhone,
          dealerId: data.dealerId,
          notes: "",
          orderNumber: data.orderNumber,
          poNumber,
        }).then(({ payload }) => {
          // const orderNumber =
          //   payload.orderFulfillmentList[0]?.fulfillment_value.orderNumber;

          const orderNumber = payload.referenceNumber || payload.orderNumber;

          if (orderNumber && payload.orderMessageList.length === 0) {
            setInfoToast({
              action: {
                callback: () => {
                  setLocation(myOrdersHash);
                },
                label: t("auth.myOrders"),
              },
              message: t("dealerOrderModal.successMessage", { orderNumber }),
            });
          } else {
            const error = new Error(
              t("dealerOrderModal.error.invalidResponse")
            );
            setErrorToast(error);
          }
        })
      );

      Promise.all(promises).finally(hide);
    },
    schema,
  });
  const { subtotal } = useTotals(normalizedData);

  return (
    <form
      className={clsx(
        "p-4 bg-paper-2 border-t border-dashed border-edge-2 gap-4",
        "md:grid md:grid-cols-11 md:items-start"
      )}
      noValidate
      onSubmit={handleForm}
    >
      <fieldset className="grid gap-2 md:col-span-4 md:p-4 md:self-stretch">
        <h3 className="font-semibold">
          {t("dealerOrderModal.customerNumber")}{" "}
          {t("dealerOrderModal.locationCode")}
        </h3>
        <p className="text-sm text-ink-2">
          {dealer?.dealerName}
          <br />
          {dealerAddress}
        </p>

        <label className="flex flex-col gap-1 md:justify-end">
          <span className="text-sm text-ink-2">PO#:</span>
          <Input
            aria-label="poNumber"
            className="bg-paper border-edge-2 md:max-w-[16rem]"
            id="do-po"
            name="poNumber"
            placeholder={t("dealerOrderModal.poPlaceholder")}
            type="text"
          />
        </label>
      </fieldset>
      <fieldset
        className={clsx(
          "grid gap-2",
          "md:col-span-4 md:p-4 md:grid-cols-2 md:gap-4"
        )}
      >
        <h3 className="font-semibold md:col-span-2">
          {t("dealerOrderModal.contactInformation")}
        </h3>
        <label className="grid gap-1">
          <span className="text-sm text-ink-2">
            {t("dealerOrderModal.firstName")}:
          </span>
          <Input
            aria-label="yourFirstName"
            autoComplete="given-name"
            className="bg-paper border-edge-2"
            id="do-po"
            name="firstName"
            placeholder={t("dealerOrderModal.firstNamePlaceholder")}
            type="text"
          />
        </label>
        <label className="grid gap-1">
          <span className="text-sm text-ink-2">
            {t("dealerOrderModal.lastName")}:
          </span>
          <Input
            aria-label="yourLastName"
            autoComplete="family-name"
            className="bg-paper border-edge-2"
            id="do-po"
            name="lastName"
            placeholder={t("dealerOrderModal.lastNamePlaceholder")}
            type="text"
          />
        </label>
        <label className="grid gap-1">
          <span className="text-sm text-ink-2">
            {t("dealerOrderModal.phone")}:
          </span>
          <Input
            aria-label="yourPhone"
            autoComplete="tel"
            className="bg-paper border-edge-2"
            id="do-po"
            name="customerPhone"
            placeholder={t("dealerOrderModal.phonePlaceholder")}
            type="text"
          />
        </label>
        <label className="grid gap-1">
          <span className="text-sm text-ink-2">
            {t("dealerOrderModal.email")}:
          </span>
          <Input
            aria-label="yourEmail"
            autoComplete="email"
            className="bg-paper border-edge-2"
            id="do-po"
            name="customerEmail"
            placeholder={t("dealerOrderModal.emailPlaceholder")}
            type="text"
          />
        </label>
      </fieldset>
      <fieldset
        className={clsx(
          "border border-edge-2 p-4 rounded-md",
          "flex flex-col h-full justify-center",
          "md:col-span-3 md:p-6"
        )}
      >
        <KeyValue className="justify-between py-2 md:py-3 border-b-edge-2 border-b mb-2 md:mb-12">
          <>{t("dealerOrderModal.total")}:</>
          <PriceDisplay fallback={t("dealerOrderModal.nationalPricing")}>
            {subtotal}
          </PriceDisplay>
        </KeyValue>
        {/* <KeyValue className="justify-between py-2 md:py-3 border-b-edge-2 border border-dashed">
          <>{t("dealerOrderModal.subtotal")}:</>
          <PriceDisplay fallback={t("dealerOrderModal.nationalPricing")}>
            {subtotal}
          </PriceDisplay>
        </KeyValue> */}
        {/* <KeyValue className="justify-between py-2 md:py-3 border-b border-ink tw-highlight">
          <> {t("dealerOrderModal.freight")}:</>
          <PriceDisplay fallback={t("dealerOrderModal.nationalPricing")}>
            {freightTotal}
          </PriceDisplay>
        </KeyValue> */}
        {/* <KeyValue className="justify-between py-2 md:py-3 mb-2 tw-highlight">
          <> {t("dealerOrderModal.total")}:</>
          <PriceDisplay fallback={t("dealerOrderModal.nationalPricing")}>
            {total}
          </PriceDisplay>
        </KeyValue> */}
        <PrimaryButton
          aria-label="placeOrder"
          busy={dealerOrderIsLoading || cartIsSaving}
          className="h-12"
          type="submit"
        >
          {t("dealerOrderModal.placeOrder")}
        </PrimaryButton>
      </fieldset>
    </form>
  );
};
